import { MAX_SATURATED_FAT_RATIO, KCAL_PER_G } from './config';
import { round6 } from './math';
import type { NutrientTargets, Sex } from './types';

/**
 * Valores de referencia de la EFSA para adultos.
 *
 * FUENTE ÚNICA de todos los números de este archivo:
 *   EFSA, "Summary of Dietary Reference Values – version 4 (September 2017)"
 *   https://www.efsa.europa.eu/sites/default/files/assets/DRV_Summary_tables_jan_17.pdf
 *   Tabla 3 (grasas y fibra), Tablas 5 y 7 (minerales), Tablas 9 y 11 (vitaminas).
 *
 * PRI = Population Reference Intake · AI = Adequate Intake. Se anota cuál es
 * cada uno porque no significan lo mismo: un AI es una estimación de consumo
 * observado, no un requerimiento demostrado.
 *
 * Aquí NO se inventa ni se redondea ningún valor. Si la EFSA no lo publica,
 * no aparece.
 */

/** 1 MJ = 238,83 kcal. Nota (a) de la Tabla 1 del mismo documento. */
export const KCAL_PER_MJ = 238.83;

/**
 * Nivel de ingesta de fitatos (mg/d) que se asume para el zinc. La EFSA da
 * cuatro escenarios porque los fitatos bloquean su absorción; no hay un valor
 * único. 600 mg/d es el escenario intermedio y el que se usa por defecto.
 */
export type PhytateLevel = 300 | 600 | 900 | 1200;

/** Tabla 5 (hombres) y Tabla 7 (mujeres), columna Zinc. PRI. */
const ZINC_PRI: Record<Sex, Record<PhytateLevel, number>> = {
  hombre: { 300: 9.4, 600: 11.7, 900: 14.0, 1200: 16.3 },
  mujer: { 300: 7.5, 600: 9.3, 900: 11.0, 1200: 12.7 },
};

/** Valores de adulto (>= 18 años) que no dependen del sexo. */
const COMMON = {
  // Tabla 3, AI
  fiber: 25, // g/d
  epaDha: 250, // mg/d
  // Tablas 5 y 7, AI
  iodine: 150, // µg/d
  manganese: 3.0, // mg/d
  phosphorus: 550, // mg/d
  potassium: 3500, // mg/d
  selenium: 70, // µg/d
  // Tablas 9 y 11, AI salvo donde se indique
  vitaminD: 15, // µg/d, AI (asumiendo síntesis cutánea mínima)
  vitaminK: 70, // µg/d, AI (solo filoquinona)
  vitaminB12: 4.0, // µg/d, AI
  folate: 330, // µg DFE/d, PRI
  riboflavin: 1.6, // mg/d, PRI
} as const;

/** Lo que sí cambia con el sexo. */
const BY_SEX: Record<
  Sex,
  {
    vitaminC: number;
    magnesium: number;
    copper: number;
    vitaminA: number;
    vitaminB6: number;
    vitaminE: number;
  }
> = {
  // Tabla 5 y Tabla 9
  hombre: {
    vitaminC: 110, // mg/d, PRI
    magnesium: 350, // mg/d, AI
    copper: 1.6, // mg/d, AI
    vitaminA: 750, // µg RE/d, PRI
    vitaminB6: 1.7, // mg/d, PRI
    vitaminE: 13, // mg α-tocoferol/d, AI
  },
  // Tabla 7 y Tabla 11
  mujer: {
    vitaminC: 95, // mg/d, PRI
    magnesium: 300, // mg/d, AI
    copper: 1.3, // mg/d, AI
    vitaminA: 650, // µg RE/d, PRI
    vitaminB6: 1.6, // mg/d, PRI
    vitaminE: 11, // mg α-tocoferol/d, AI
  },
};

/** Tabla 9 y 11: definidos por MJ de energía, no por día. PRI. */
const PER_MJ = {
  thiamin: 0.1, // mg/MJ
  niacin: 1.6, // mg EN/MJ
} as const;

export interface MicroTargetInput {
  sex: Sex;
  ageYears: number;
  /** Objetivo calórico: la tiamina y la niacina se definen por MJ ingerido. */
  kcal: number;
  /** Solo se usa en mujeres. La EFSA no fija una edad de corte. */
  postmenopausal?: boolean;
  phytateLevel?: PhytateLevel;
}

export function microTargets(input: MicroTargetInput): NutrientTargets {
  const { sex, ageYears, kcal, postmenopausal = false, phytateLevel = 600 } = input;

  if (!(ageYears >= 18)) {
    throw new Error('La tabla de referencia de la EFSA usada aquí es de adultos (≥ 18 años)');
  }
  if (!(kcal > 0)) throw new Error('El objetivo calórico debe ser mayor que 0');

  const bySex = BY_SEX[sex];
  const mj = kcal / KCAL_PER_MJ;

  return {
    ...COMMON,
    ...bySex,

    // Tablas 5 y 7: 1000 mg de 18 a 24; 950 a partir de 25. PRI.
    calcium: ageYears <= 24 ? 1000 : 950,

    // Tabla 5: hombre 11 mg. Tabla 7: mujer premenopáusica 16, posmenopáusica 11. PRI.
    iron: sex === 'hombre' ? 11 : postmenopausal ? 11 : 16,

    zinc: ZINC_PRI[sex][phytateLevel],

    thiamin: round6(mj * PER_MJ.thiamin),
    niacin: round6(mj * PER_MJ.niacin),
  };
}

/**
 * Topes que no conviene superar (SPEC 6.A).
 *
 * Ojo: estos dos NO salen de la tabla de la EFSA. La EFSA tenía la evaluación
 * del sodio en curso en ese documento y para las grasas saturadas dice
 * literalmente "as low as possible", sin cifra. Los números son los que fija
 * el spec del producto, no una referencia oficial.
 */
export function upperLimits(kcal: number): NutrientTargets {
  if (!(kcal > 0)) throw new Error('El objetivo calórico debe ser mayor que 0');
  return {
    sodium: 2000, // mg/d
    saturatedFat: round6((kcal * MAX_SATURATED_FAT_RATIO) / KCAL_PER_G.fat), // g/d
  };
}
